/**
 * Проект-витрина для меню разработки: одна большая комната, в которой на полу
 * рядами стоят все модели мебели, декора и напольной сантехники из каталога —
 * варианты одного типа рядом, чтобы сравнить их в 3D.
 */
import { CATALOG, CatalogAttach, type CatalogItem } from "@/shared/catalog";
import {
	CatalogSectionKind,
	getEmptyPlanLayers,
	LayerKind,
	uid,
	type FloorItem,
	type PlanLayers,
} from "@/shared/plan";

const SHOWCASE_SECTIONS = [
	CatalogSectionKind.Furniture,
	CatalogSectionKind.Decor,
	CatalogSectionKind.Plumbing,
] as const;

const ROW_WIDTH = 16000;
const GAP = 600;
const MARGIN = 1200;
const WALL_THICKNESS = 200;
const WALL_HEIGHT = 2700;

/** Варианты одного типа подряд, порядок типов — как в каталоге. */
const byFamily = (items: readonly CatalogItem[]): CatalogItem[] => {
	const order = new Map<string, number>();
	items.forEach((item, index) => {
		const key = item.family ?? item.id;
		if (!order.has(key)) order.set(key, index);
	});
	return items.toSorted(
		(a, b) => (order.get(a.family ?? a.id) ?? 0) - (order.get(b.family ?? b.id) ?? 0),
	);
};

export const buildCatalogShowcaseLayers = (): PlanLayers => {
	const layers = getEmptyPlanLayers();
	let x = 0;
	let y = 0;
	let rowDepth = 0;
	let maxX = 0;

	for (const key of SHOWCASE_SECTIONS) {
		const section = CATALOG[key];
		const floorItems = (section.items as readonly CatalogItem[]).filter(
			(item) => item.attach === CatalogAttach.Everywhere,
		);
		for (const item of byFamily(floorItems)) {
			if (x > 0 && x + item.width > ROW_WIDTH) {
				x = 0;
				y += rowDepth + GAP;
				rowDepth = 0;
			}
			const placed: FloorItem = {
				id: uid("i"),
				ref: { catalog: key, item: item.id },
				x: MARGIN + x + item.width / 2,
				y: MARGIN + y + item.depth / 2,
				rotation: 0,
				// Всё на полу: настольную лампу и накладную раковину так проще разглядеть
				elevation: 0,
			};
			const layer = section.kind === LayerKind.Plumbing ? layers.plumbing : layers.furniture;
			layer.items[placed.id] = placed;
			x += item.width + GAP;
			maxX = Math.max(maxX, x - GAP);
			rowDepth = Math.max(rowDepth, item.depth);
		}
	}

	// Оси стен — снаружи чистого контура на полтолщины
	const half = WALL_THICKNESS / 2;
	const right = MARGIN * 2 + maxX + half;
	const bottom = MARGIN * 2 + y + rowDepth + half;
	const corners = [
		{ x: -half, y: -half },
		{ x: right, y: -half },
		{ x: right, y: bottom },
		{ x: -half, y: bottom },
	].map((point) => ({ id: uid("n"), ...point }));
	for (const node of corners) layers.walls.nodes[node.id] = node;
	corners.forEach((node, index) => {
		const id = uid("w");
		layers.walls.walls[id] = {
			id,
			a: node.id,
			b: corners[(index + 1) % corners.length].id,
			thickness: WALL_THICKNESS,
			height: WALL_HEIGHT,
		};
	});
	return layers;
};
